import React, { useState, useRef, useEffect, useCallback } from 'react';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { notify, confirm } from '../../context/FeedbackContext';
import './ProductForm.css';
import { FiEdit2 } from 'react-icons/fi';
import { getFilteredNavItemsWithIcons, getRouteFromItem } from '../../config/routes';

function ProductEditForm({ onNavigate, product }) {
  const p = product || {};
  const { restaurants, selectedRestaurant } = useRestaurant() || {};
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    tenantCode: '',
    productName: p.itemName || '',
    productDescription: p.itemDesc || '',
    catReference: p.catReference || '',
    basePrice: p.basePrice !== null ? parseFloat(p.basePrice).toFixed(2) : '0.00',
    membershipPrice: p.memberPrice !== null ? parseFloat(p.memberPrice).toFixed(2) : '0.00',
    festivalPrice: p.festivalPrice !== null ? parseFloat(p.festivalPrice).toFixed(2) : '0.00',
    seasonal: p.seasonal === 1 ? 'seasonal' : 'regular',
    status: p.status === 1 ? 'Published' : 'Unpublished',
    printerName: p.printerName || '',
    printerReference: p.printerReference || '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    p.itemPic || '');  const [isSubmitting, setIsSubmitting] = useState(false);
  const imageInputRef = useRef(null);

  // 打印机列表
  const [prinList, setPrinList] = useState([]);

  const fetchCategories = useCallback(async () => {
    if (!selectedRestaurant?.reference) return;
    try {
      const res = await fetch(buildApiUrl(`/catalog/categories/options?tenantReference=${selectedRestaurant.reference}`), { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.code === 200 && data.data) {
        setCategories(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch categories', err);
    }
  }, [selectedRestaurant?.reference]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (selectedRestaurant) {
      setForm(prev => ({ ...prev, tenantCode: selectedRestaurant.code }));
    }
  }, [selectedRestaurant]);

  // 查询打印机
  const getPrintIn = async (tenantRef) => {
    if (!tenantRef) return;
    try {
      const res = await fetch(
        buildApiUrl(`/setting/printers/by-tenant?tenantReference=${tenantRef}`),
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      if (data.code === 200) {
        setPrinList(data.data);
      }
    } catch (err) {
      notify(err, 'error');
      console.error("Failed to fetch printers", err);
    }
  };

  useEffect(() => {
    getPrintIn(selectedRestaurant?.reference);
  }, [selectedRestaurant]); // eslint-disable-line react-hooks/exhaustive-deps

  // 打印机选择
  const handleFormChangePrint = (e) => {
    const selectedReference = e.target.value;
    const selectedPrinter = prinList.find(pr => pr.reference === selectedReference);
    setForm(prev => ({
      ...prev,
      printerReference: selectedReference,
      printerName: selectedPrinter?.printerName || '',
    }));
    setFormErrors(prev => ({ ...prev, printerReference: undefined }));
  };

  // 初始加载标记，区分 API 回显和用户手动切换分类
  const initRef = useRef(false);
  useEffect(() => {
    if (p.printerReference) {
      initRef.current = true;
      return;
    }
    if (!initRef.current) {
      if (form.catReference && prinList.length > 0) {
        const selectedCat = categories.find(c => c.reference === form.catReference);
        if (selectedCat?.printerReference) {
          const matchedPrinter = prinList.find(pr => pr.reference === selectedCat.printerReference);
          if (matchedPrinter) {
            setForm(prev => ({ ...prev, printerReference: matchedPrinter.reference, printerName: matchedPrinter.printerName }));
          }
        }
      }
      initRef.current = true;
      return;
    }
    // 用户手动切换分类：按分类自动关联打印机
    const selectedCat = categories.find(c => c.reference === form.catReference);
    if (selectedCat?.printerReference) {
      const matchedPrinter = prinList.find(pr => pr.reference === selectedCat.printerReference);
      if (matchedPrinter) {
        setForm(prev => ({ ...prev, printerReference: matchedPrinter.reference, printerName: matchedPrinter.printerName }));
      }
    } else {
      setForm(prev => ({ ...prev, printerReference: '', printerName: '' }));
    }
  }, [form.catReference, categories, prinList]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
   
    
    if (categories.length > 0 && !form.catReference) {
      setForm(prev => ({ ...prev, catReference: categories[0].reference }));
    }
  }, [categories]); // eslint-disable-line react-hooks/exhaustive-deps

  const DESC_MAX = 50;
  const NAME_MAX = 50;

  const validate = () => {
    const errors = {};

    if (!form.productName.trim()) {
      errors.productName = 'Product name is required.';
    } else if (form.productName.trim().length > NAME_MAX) {
      errors.productName = `Product name cannot exceed ${NAME_MAX} characters.`;
    }

    if (!form.productDescription.trim()) {
      errors.productDescription = 'Product description is required.';
    } else if (form.productDescription.trim().length > DESC_MAX) {
      errors.productDescription = `Description cannot exceed ${DESC_MAX} characters.`;
    }

    if (!form.catReference) {
      errors.catReference = 'Please select a category.';
    }

    if (!form.printerReference) {
      errors.printerReference = 'Please select a printer.';
    }

    if (form.basePrice === '' || isNaN(parseFloat(form.basePrice)) || parseFloat(form.basePrice) < 0) {
      errors.basePrice = 'Please enter a valid base price.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setFormErrors(prev => ({ ...prev, [e.target.name]: undefined }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };


  const handleUpdate = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const productPayload = {
        reference: p.reference,
        tenantReference: selectedRestaurant?.reference,
        tenantCode: form.tenantCode,
        catReference: form.catReference,
        itemType: 0,
        itemName: form.productName.trim(),
        itemDesc: form.productDescription.trim(),
        basePrice: parseFloat(form.basePrice) || 0,
        memberPrice: parseFloat(form.membershipPrice) || 0,
        festivalPrice: parseFloat(form.festivalPrice) || 0,
        seasonal: form.seasonal === 'seasonal' ? 1 : 0,
        status: form.status === 'Published' ? 1 : 0,
        printerReference: form.printerReference || null,
      };

      const formData = new FormData();
      formData.append('product', JSON.stringify(productPayload));
      if (imageFile) formData.append('image', imageFile);

      const res = await fetch(buildApiUrl('/product/simple/edit'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      const data = await res.json();
      if (data.code === 200) {
        notify('Product updated successfully!', 'success');
        onNavigate('Product');
      } else {
        notify('Failed to update product: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error('Error updating product:', err);
      notify('Failed to update product: ' + err.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!(await confirm('Are you sure you want to delete this product?'))) return;
    try {
      const sku = p.sku;
      const res = await fetch(buildApiUrl(`/catalog/magento/delete?sku=${sku}`), {
        method: 'POST',
      });
      if (res.ok) {
        notify('Product deleted successfully!', 'success');
        onNavigate('Product');
      } else {
        notify('Failed to delete product.', 'error');
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      notify('Failed to delete product: ' + err.message, 'error');
    }
  };

  const handleCancel = () => {
    onNavigate('Product');
  };

  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (
    <div className="pf-wrapper">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item =>
            (
              <li key={item} className={item === 'Product' ? 'active' : ''} onClick={() => onNavigate(getRouteFromItem(item))}>
                <span className="nav-icon">{navIcons[item]}</span> {item}
              </li>
            )
          )}
        </ul>
      </nav>

      <main className="pf-main">
        <h1 className="pf-title">Edit Simple Product</h1>

        <div className="pf-card">
          {/* Row: Product Name + Categories */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Product Name <span className="pf-required">*</span></label>
              <input
                className={`pf-input${formErrors.productName ? ' pf-input-error' : ''}`}
                name="productName"
                value={form.productName}
                onChange={handleChange}
                placeholder="Enter product name"
              />
              {formErrors.productName
                ? <span className="pf-field-error">{formErrors.productName}</span>
                : <span className="pf-hint">A product name is required and recommended to be unique</span>
              }
            </div>
            <div className="pf-field">
              <label>Categories <span className="pf-required">*</span></label>
              <select className={`pf-select${formErrors.catReference ? ' pf-input-error' : ''}`} name="catReference" value={form.catReference} onChange={handleChange}>
                {categories.map(c => (
                  <option key={c.reference} value={c.reference}>{c.catName}</option>
                ))}
              </select>
              {formErrors.catReference && <span className="pf-field-error">{formErrors.catReference}</span>}
            </div>
          </div>

          {/* Product Description */}
          <div className="pf-field">
            <label>Product Description <span className="pf-required">*</span></label>
            <div className="pf-editor">
              <textarea
                className={`pf-textarea${formErrors.productDescription ? ' pf-input-error' : ''}`}
                name="productDescription"
                value={form.productDescription}
                onChange={handleChange}
                placeholder="Provide a detailed description of your product"
                maxLength={DESC_MAX + 1}
              />
            </div>
            {formErrors.productDescription && <span className="pf-field-error">{formErrors.productDescription}</span>}
          </div>

          {/* Image */}
          <div className="pf-field">
            {imagePreview ? (
              <div className="pf-img-edit-wrap">
                <img src={imagePreview} alt="Product" className="pf-img-thumb" />
                <button type="button" className="pf-img-edit-icon" onClick={() => imageInputRef.current.click()}>
                  <FiEdit2 />
                </button>
              </div>
            ) : (
              <button type="button" className="pf-upload-btn" onClick={() => imageInputRef.current.click()}>
                Upload Product Images
              </button>
            )}
            <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
          </div>

          {/* Row: Base Price + Seasonal */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Base Price <span className="pf-required">*</span></label>
              <input className={`pf-input${formErrors.basePrice ? ' pf-input-error' : ''}`} name="basePrice" type="number" step="0.01" min="0" value={form.basePrice} onChange={handleChange} />
              {formErrors.basePrice && <span className="pf-field-error">{formErrors.basePrice}</span>}
            </div>
            <div className="pf-field">
              <label>Seasonal</label>
              <select className="pf-select" name="seasonal" value={form.seasonal} onChange={handleChange}>
                <option value="regular">regular</option>
                <option value="seasonal">seasonal</option>
              </select>
            </div>
          </div>

          {/* Membership Price + Festival Price */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Membership Price</label>
              <input className="pf-input" name="membershipPrice" type="number" step="0.01" min="0" value={form.membershipPrice} onChange={handleChange} />
            </div>
            <div className="pf-field">
              <label>Festival Price</label>
              <input className="pf-input" name="festivalPrice" type="number" step="0.01" min="0" value={form.festivalPrice} onChange={handleChange} />
            </div>
          </div>

          {/* Status + Printer */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Status</label>
              <select className="pf-select" name="status" value={form.status} onChange={handleChange}>
                <option>Published</option>
                <option>Unpublished</option>
              </select>
            </div>
            <div className="pf-field">
              <label>Printer <span className="pf-required">*</span></label>
              <select
                className={`pf-select${formErrors.printerReference ? ' pf-input-error' : ''}`}
                name="printerReference"
                value={form.printerReference || ''}
                onChange={handleFormChangePrint}
              >
                <option value="">-- Select Printer --</option>
                {form.printerName && !prinList.some(pr => pr.printerName === form.printerName) && (
                  <option value={form.printerReference}>{form.printerName}</option>
                )}
                {prinList.map((printer) => (
                  <option key={printer.id} value={printer.reference}>
                    {printer.printerName}
                  </option>
                ))}
              </select>
              {formErrors.printerReference && <span className="pf-field-error">{formErrors.printerReference}</span>}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="pf-actions">
          <button className="pf-cancel-btn" onClick={handleCancel} disabled={isSubmitting}>Cancel</button>
          <button className="pf-save-btn" onClick={handleUpdate} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </main>
    </div>
  );
}

export default ProductEditForm;
